import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useCart } from "../context/CartContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/offers", label: "Offers" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-brown/70 border-b border-border" : "bg-transparent"
        }`}
      >
        <nav className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[74px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 sm:gap-3 shrink-0">
            <img
              src={BRAND.mascot}
              alt={BRAND.name}
              className="h-11 w-11 sm:h-12 sm:w-12 object-contain rounded-lg shrink-0"
            />
            <span className="flex flex-col leading-none">
              <span className="font-heading uppercase text-cream text-lg sm:text-xl tracking-tight">
                the <span className="text-orange">kukkad</span> co.
              </span>
              <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-0.5">
                Grilled · Fried · Curried · Loaded
              </span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                    isActive ? "text-orange" : "text-cream/70 hover:text-cream"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cart-button"
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 bg-orange text-primary-foreground font-bold uppercase text-sm px-4 py-2.5 rounded-md transition-transform hover:scale-105 active:scale-95"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-2 -right-2 bg-chick text-brown text-xs font-heading w-5 h-5 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="lg:hidden text-cream p-1"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X /> : <Menu />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-[74px] inset-x-0 z-40 lg:hidden bg-brown-deep border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `py-3 px-2 font-heading text-2xl uppercase ${isActive ? "text-orange" : "text-cream"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
